import { useState, useEffect } from 'react';
import AppointmentForm from './AppointmentForm';
import AdminPage from './AdminPage';
import Login from './components/Login';
import { motion } from 'framer-motion';

// Schema Markup Component for SEO
const SchemaMarkup = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "All Chimney Solution",
    "image": "https://yourdomain.com/logo.png",
    "description": "Professional chimney cleaning, inspection, and repair services in Boston",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "123 Main Street",
      "addressLocality": "Boston",
      "addressRegion": "MA",
      "postalCode": "02101",
      "addressCountry": "US"
    },
    "telephone": "+1-555-123-4567",
    "email": "info@allchimneysolution.com",
    "url": "https://yourdomain.com",
    "priceRange": "$$",
    "areaServed": [
      {
        "@type": "City",
        "name": "Boston"
      },
      {
        "@type": "City",
        "name": "Cambridge"
      },
      {
        "@type": "City",
        "name": "Brookline"
      },
      {
        "@type": "City",
        "name": "Newton"
      },
      {
        "@type": "City",
        "name": "Waltham"
      }
    ],
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "08:00",
        "closes": "18:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": "Saturday",
        "opens": "09:00",
        "closes": "16:00"
      }
    ],
    "sameAs": [
      "https://www.facebook.com/allchimneysolution",
      "https://www.yelp.com/biz/all-chimney-solution-boston"
    ]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
};

function App() {
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentPage, setCurrentPage] = useState('home'); // 'home', 'about', 'contact', 'admin'
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const authStatus = localStorage.getItem('isAdminAuthenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('isAdminAuthenticated');
    setCurrentPage('home');
  };

  // Show login page if trying to access admin without authentication
  if (currentPage === 'admin' && !isAuthenticated) {
    return <Login onLogin={setIsAuthenticated} />;
  }

  // Show admin page if authenticated
  if (currentPage === 'admin') {
    return (
      <div>
        <div className="fixed top-4 right-4 flex gap-2 z-50">
          <button
            onClick={() => setCurrentPage('home')}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors shadow-lg"
          >
            Back to Home
          </button>
          <button
            onClick={handleLogout}
            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors shadow-lg"
          >
            Logout
          </button>
        </div>
        <AdminPage />
      </div>
    );
  }

  return (
    <>
      <SchemaMarkup />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-sky-50 to-cyan-50 dark:from-slate-900 dark:via-blue-900 dark:to-slate-900">
        {/* Navigation Bar */}
        <nav className="sticky top-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md shadow-lg border-b border-sky-100 dark:border-blue-800 z-40">
          <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-sky-600 to-blue-600 bg-clip-text text-transparent">
              All Chimney Solution
            </h1>
            
            <div className="flex gap-4 items-center">
              <button
                onClick={() => setCurrentPage('home')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  currentPage === 'home'
                    ? 'bg-sky-500 text-white'
                    : 'text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-blue-800'
                }`}
              >
                Home
              </button>
              <button
                onClick={() => setCurrentPage('about')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  currentPage === 'about'
                    ? 'bg-sky-500 text-white'
                    : 'text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-blue-800'
                }`}
              >
                About Us
              </button>
              <button
                onClick={() => setCurrentPage('contact')}
                className={`px-4 py-2 rounded-lg transition-all ${
                  currentPage === 'contact'
                    ? 'bg-sky-500 text-white'
                    : 'text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-blue-800'
                }`}
              >
                Contact
              </button>
              <button
                onClick={() => setCurrentPage('admin')}
                className="px-4 py-2 bg-gradient-to-r from-sky-600 to-blue-600 text-white rounded-lg hover:from-sky-700 hover:to-blue-700 transition-all shadow-lg"
              >
                Admin
              </button>
            </div>
          </div>
        </nav>

        {/* Home Page */}
        {currentPage === 'home' && (
          <div className="py-12 px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
              >
                <h1 className="text-6xl font-bold text-sky-700 dark:text-sky-300 mb-4">
                  All Chimney Solution
                </h1>
                <p className="text-xl text-sky-600 dark:text-sky-400 max-w-2xl mx-auto">
                  Professional chimney cleaning, inspection, and repair services in Boston. Licensed, insured, and available 24/7 for emergencies.
                </p>
              </motion.div>
              
              <AppointmentForm onSuccess={() => setRefreshKey(refreshKey + 1)} />
            </div>
          </div>
        )}

        {/* About Us Page */}
        {currentPage === 'about' && (
          <div className="py-12 px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 border border-sky-100 dark:border-blue-700"
              >
                <h1 className="text-4xl font-bold text-sky-700 dark:text-sky-300 mb-6">
                  About All Chimney Solution
                </h1>
                
                <div className="space-y-6 text-slate-700 dark:text-slate-300">
                  <p className="text-lg leading-relaxed">
                    Welcome to All Chimney Solution, your trusted partner in chimney care and home safety. With over 20 years of experience in the chimney industry, we've built a reputation for delivering exceptional service to homeowners throughout the Boston area.
                  </p>
                  
                  <h2 className="text-2xl font-semibold text-sky-600 dark:text-sky-400 mt-8">Our Mission</h2>
                  <p className="text-lg leading-relaxed">
                    Our mission is to provide professional, reliable, and affordable chimney services that keep your home safe and your family warm. We believe that every chimney deserves expert care, and we're committed to delivering excellence in every job.
                  </p>
                  
                  <h2 className="text-2xl font-semibold text-sky-600 dark:text-sky-400 mt-8">Why Choose Us?</h2>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="text-sky-500 font-bold text-xl">✓</span>
                      <span>Licensed and insured professionals</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-sky-500 font-bold text-xl">✓</span>
                      <span>Fast and reliable service</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-sky-500 font-bold text-xl">✓</span>
                      <span>Competitive pricing</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-sky-500 font-bold text-xl">✓</span>
                      <span>24/7 emergency services</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-sky-500 font-bold text-xl">✓</span>
                      <span>Guaranteed customer satisfaction</span>
                    </li>
                  </ul>
                  
                  <h2 className="text-2xl font-semibold text-sky-600 dark:text-sky-400 mt-8">Our Services</h2>
                  <ul className="space-y-2">
                    <li>• Chimney Cleaning</li>
                    <li>• Chimney Inspection</li>
                    <li>• Chimney Repair</li>
                    <li>• Chimney Cap Installation</li>
                    <li>• Fireplace Maintenance</li>
                    <li>• Chimney Relining</li>
                  </ul>
                </div>
              </motion.div>
            </div>
          </div>
        )}

        {/* Contact Page */}
        {currentPage === 'contact' && (
          <div className="py-12 px-4">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-8 border border-sky-100 dark:border-blue-700"
              >
                <h1 className="text-4xl font-bold text-sky-700 dark:text-sky-300 mb-8">
                  Contact Us
                </h1>
                
                <div className="grid md:grid-cols-2 gap-8">
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-sky-600 dark:text-sky-400 mb-2">
                        Phone
                      </h3>
                      <a href="tel:+15551234567" className="text-lg text-sky-600 dark:text-sky-400 hover:underline">
                        (555) 123-4567
                      </a>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-semibold text-sky-600 dark:text-sky-400 mb-2">
                        Email
                      </h3>
                      <a href="mailto:info@allchimneysolution.com" className="text-lg text-sky-600 dark:text-sky-400 hover:underline">
                        info@allchimneysolution.com
                      </a>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-semibold text-sky-600 dark:text-sky-400 mb-2">
                        Address
                      </h3>
                      <p className="text-lg text-slate-700 dark:text-slate-300">
                        123 Main Street<br />
                        Boston, MA 02101
                      </p>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-semibold text-sky-600 dark:text-sky-400 mb-2">
                        Hours
                      </h3>
                      <p className="text-lg text-slate-700 dark:text-slate-300">
                        Monday - Friday: 8AM - 6PM<br />
                        Saturday: 9AM - 4PM<br />
                        <span className="text-sky-600 dark:text-sky-400 font-semibold">Emergency: 24/7</span>
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-xl font-semibold text-sky-600 dark:text-sky-400 mb-4">
                      Quick Appointment
                    </h3>
                    <p className="text-slate-700 dark:text-slate-300 mb-4">
                      Book an appointment to schedule your chimney service today! We serve all of the Boston area.
                    </p>
                    <button
                      onClick={() => setCurrentPage('home')}
                      className="w-full px-6 py-3 bg-gradient-to-r from-sky-500 to-blue-600 text-white font-semibold rounded-lg hover:from-sky-600 hover:to-blue-700 transition-all shadow-lg"
                    >
                      Book Now
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default App;
